import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import {
  Stethoscope, AlertTriangle, CheckCircle2, RefreshCw, Clock, ArrowRight,
  ShieldCheck, Activity, Users, Banknote, LayoutGrid, BadgeAlert, Sparkles,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /clinician/visit-brief/:appointmentId — Blueprint Decision #36.
 *
 * Per-visit prep brief generated by the LLM from the patient's last 3
 * clinical notes + diagnosis + active medications + active care plan.
 */
export default function VisitBrief() {
  const { appointmentId } = useParams();
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);

  const loadCached = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/ai/practice-coach/visit-brief/${appointmentId}`, getAuthHeader());
      if (data?.cached) setData(data);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  }, [appointmentId, getAuthHeader]);

  useEffect(() => { loadCached(); }, [loadCached]);

  const generate = async () => {
    setRegenerating(true);
    try {
      const { data } = await axios.post(
        `${API}/ai/practice-coach/visit-brief`,
        { appointment_id: appointmentId },
        { ...getAuthHeader(), timeout: 75000 },
      );
      setData(data);
      toast.success('Brief generated.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Brief generation failed');
    } finally {
      setRegenerating(false);
    }
  };

  const navItems = [
    { icon: LayoutGrid, label: 'Visit Brief', active: true },
    { icon: ShieldCheck, label: 'Trust Portal',  href: '/trust' },
    { icon: Activity,    label: 'Executive',     href: '/operations/executive' },
    { icon: Users,       label: 'Workforce',     href: '/agency/find-clinicians' },
    { icon: Banknote,    label: 'Revenue Cycle', href: '/clinician/billing' },
  ];

  const brief = data?.brief;

  return (
    <DashboardShell
      navItems={navItems}
      title="Pre-visit Brief"
      subtitle={data?.appointment_reason ? `Visit reason: ${data.appointment_reason}` : "Practice Coach reviews the chart and tells you what to ask before you walk in."}
      testId="visit-brief-page"
      rightSlot={
        <Button onClick={generate} disabled={regenerating} className="cn-btn cn-btn-primary" data-testid="visit-brief-generate">
          {regenerating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {data ? 'Regenerate' : 'Generate brief'}
        </Button>
      }
    >
      {loading ? (
        <div className="cn-card h-48 animate-pulse" />
      ) : !data ? (
        <div className="cn-card p-10 text-center" data-testid="visit-brief-empty">
          <Stethoscope className="w-12 h-12 text-cn-text-muted/40 mx-auto mb-4" />
          <h2 className="font-display font-semibold text-cn-text dark:text-white text-xl">No brief yet</h2>
          <p className="text-sm text-cn-text-muted mt-2 max-w-md mx-auto">
            Click <strong>Generate brief</strong> above. CaraLoom reads the patient&rsquo;s last
            3 notes + active care plan + medications and returns a focused pre-visit checklist.
          </p>
          <p className="text-xs text-cn-text-muted/70 mt-3">Takes 8-15 seconds while the LLM thinks.</p>
        </div>
      ) : (
        <>
          {/* META STRIP */}
          <div className="cn-card p-5 mb-5 flex items-center gap-5 flex-wrap" data-testid="visit-brief-meta">
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">Date</div>
              <div className="font-semibold text-cn-text">{data.appointment_date || '—'}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">Time</div>
              <div className="font-semibold text-cn-text">{data.appointment_time || '—'}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">Est. duration</div>
              <div className="font-semibold text-cn-primary inline-flex items-center gap-1.5">
                <Clock className="w-4 h-4" /> {brief?.estimated_minutes || '—'} min
              </div>
            </div>
            <div className="ml-auto inline-flex items-center gap-2">
              {data.ai_unavailable ? (
                <span className="cn-trust-badge text-amber-700 border-amber-200 bg-amber-50">
                  <BadgeAlert className="w-3 h-3" /> Heuristic fallback
                </span>
              ) : (
                <span className="cn-trust-badge text-emerald-700 border-emerald-200 bg-emerald-50">
                  <CheckCircle2 className="w-3 h-3" /> LLM-generated
                </span>
              )}
            </div>
          </div>

          {/* GRID */}
          <div className="grid lg:grid-cols-2 gap-4">
            {/* Focus areas */}
            <div className="cn-card p-6" data-testid="brief-focus-areas">
              <h3 className="font-display font-semibold text-lg text-cn-text dark:text-white mb-3 inline-flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-cn-primary" /> Focus areas
              </h3>
              <ul className="space-y-2">
                {(brief?.focus_areas || []).map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-cn-text" data-testid={`brief-focus-${i}`}>
                    <span className="w-1.5 h-1.5 rounded-full bg-cn-primary mt-2 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Vitals to check */}
            <div className="cn-card p-6" data-testid="brief-vitals">
              <h3 className="font-display font-semibold text-lg text-cn-text dark:text-white mb-3 inline-flex items-center gap-2">
                <Activity className="w-4 h-4 text-cn-primary" /> Vitals to capture
              </h3>
              <div className="flex flex-wrap gap-2">
                {(brief?.vitals_to_check || []).map((v, i) => (
                  <span key={i} className="cn-trust-badge text-cn-primary border-cn-primary/30 bg-cn-primary/5" data-testid={`brief-vital-${i}`}>{v}</span>
                ))}
              </div>
            </div>

            {/* Questions to ask */}
            <div className="cn-card p-6 lg:col-span-2" data-testid="brief-questions">
              <h3 className="font-display font-semibold text-lg text-cn-text dark:text-white mb-3">Questions to ask</h3>
              <ol className="space-y-2.5">
                {(brief?.questions_to_ask || []).map((q, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm" data-testid={`brief-question-${i}`}>
                    <span className="w-6 h-6 rounded-full bg-cn-primary/10 text-cn-primary text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                    <span className="text-cn-text">{q}</span>
                  </li>
                ))}
              </ol>
            </div>

            {/* Red flags */}
            <div className="cn-card p-6 border-amber-200 bg-amber-50/30 lg:col-span-2" data-testid="brief-red-flags">
              <h3 className="font-display font-semibold text-lg text-amber-800 mb-3 inline-flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600" /> Red flags — escalate if seen
              </h3>
              <ul className="grid sm:grid-cols-2 gap-2">
                {(brief?.red_flags || []).map((r, i) => (
                  <li key={i} className="text-sm text-amber-900 flex items-start gap-2" data-testid={`brief-red-flag-${i}`}>
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <p className="text-[11px] text-cn-text-muted mt-5" data-testid="visit-brief-generated-at">
            Generated {data.generated_at ? new Date(data.generated_at).toLocaleString() : '—'} ·
            Practice Coach engine: <code>{data.engine}</code> ·
            Brief is decision support — not a substitute for clinical judgment.
          </p>

          <div className="mt-6 flex gap-3">
            <Link to="/clinician/dashboard" className="cn-btn cn-btn-ghost">
              <ArrowRight className="w-4 h-4 rotate-180" /> Back to dashboard
            </Link>
          </div>
        </>
      )}
    </DashboardShell>
  );
}
